import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { API_URL, GOOGLE_SHEETS_URL } from '../../config/api';
import { APPOINTMENT_TITLES, OFFICES } from '../../constants/calendar';
import ProductionSchedule from '../WorkOrderProfile/ProductionSchedule';
import EditableCell from '../EditableCell';
import './DateRangeReport.css';
import { useOrderBalance, formatCurrency } from '../hooks/useOrderBalance';
import { useLookoutList } from '../../utils/lookoutList';
import { filterNextRooms } from '../../utils/roomUtils';

// Maps the report's office display labels to the OFFICES keys in constants/calendar.js
const OFFICE_KEY_BY_LABEL = { Pomona: 'pomona', Lakewood: 'lakewood', Israel: 'israel' };


const DateRangeReport = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [offices, setOffices] = useState([]); // Pre-report filter - filter by office (staff location)
  const [appointmentTypes, setAppointmentTypes] = useState([]); // Pre-report filter for appointment types
  const [selectedStylists, setSelectedStylists] = useState([]); // Pre-report filter - disabled until an office is chosen
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [expandedOrderNo, setExpandedOrderNo] = useState(null);
  const [collapsedDays, setCollapsedDays] = useState(new Set());
  const [showLegend, setShowLegend] = useState(false);
  const [showReportSummary, setShowReportSummary] = useState(true); 

  // Toggle day collapse
  const toggleDayCollapse = (day) => {
    setCollapsedDays(prev => {
      const newSet = new Set(prev);
      if (newSet.has(day)) {
        newSet.delete(day);
      } else {
        newSet.add(day);
      }
      return newSet;
    });
  };

  // Office options (filters appointments by staff MeetingWith)
  const officeOptions = ['Pomona', 'Lakewood', 'Israel'];

  // Handle office checkbox toggle
  const handleOfficeToggle = (office) => {
    setOffices(prev => {
      if (prev.includes(office)) {
        return prev.filter(o => o !== office);
      } else {
        return [...prev, office];
      }
    });
  };

  // Handle appointment type checkbox toggle
  const handleAppointmentTypeToggle = (type) => {
    setAppointmentTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Stylist options are derived from whichever office(s) are selected above
  // (constants/calendar.js OFFICES.<key>.staff). Empty until an office is chosen.
  const availableStylists = useMemo(() => {
    if (offices.length === 0) return [];
    const names = new Set();
    offices.forEach(officeLabel => {
      const key = OFFICE_KEY_BY_LABEL[officeLabel];
      (OFFICES[key]?.staff || []).forEach(person => names.add(person.name));
    });
    return Array.from(names).sort();
  }, [offices]);

  const isStylistFilterEnabled = offices.length > 0;

  // Drop any selected stylists that fall out of scope when the office selection changes
  useEffect(() => {
    setSelectedStylists(prev => prev.filter(name => availableStylists.includes(name)));
  }, [availableStylists]);

  const handleStylistToggle = (name) => {
    if (!isStylistFilterEnabled) return;
    setSelectedStylists(prev => (
      prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name]
    ));
  };

  // Dropdown open states
  const [isOfficeDropdownOpen, setIsOfficeDropdownOpen] = useState(false);
  const [isAppointmentDropdownOpen, setIsAppointmentDropdownOpen] = useState(false);
  const [isStylistDropdownOpen, setIsStylistDropdownOpen] = useState(false);
  const officeDropdownRef = useRef(null);
  const appointmentDropdownRef = useRef(null);
  const stylistDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (officeDropdownRef.current && !officeDropdownRef.current.contains(event.target)) {
        setIsOfficeDropdownOpen(false);
      }
      if (appointmentDropdownRef.current && !appointmentDropdownRef.current.contains(event.target)) {
        setIsAppointmentDropdownOpen(false);
      }
      if (stylistDropdownRef.current && !stylistDropdownRef.current.contains(event.target)) {
        setIsStylistDropdownOpen(false);
      }
    };

    if (isOfficeDropdownOpen || isAppointmentDropdownOpen || isStylistDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOfficeDropdownOpen, isAppointmentDropdownOpen, isStylistDropdownOpen]);

  // Post-report filter states (applied client-side)
  const [filters, setFilters] = useState({
    roomNo: '',
    assignedWorker: '',
    customerName: '',
    meetingWith: ''
  });
  const [filteredData, setFilteredData] = useState(null);
  const [hideCompleted, setHideCompleted] = useState(false);
  const [hiddenOrders, setHiddenOrders] = useState(new Set());
  const [hiddenAppointments, setHiddenAppointments] = useState(new Set());
  const [hiddenDays, setHiddenDays] = useState(new Set());
  const [customNextRooms, setCustomNextRooms] = useState({});

  // localStorage key for persisting report state
  const STORAGE_KEY = 'dateRangeReportState';

  // Load saved state from localStorage on component mount
  useEffect(() => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        if (parsed.startDate) setStartDate(parsed.startDate);
        if (parsed.endDate) setEndDate(parsed.endDate);
        if (parsed.offices) setOffices(parsed.offices);
        if (parsed.appointmentTypes) setAppointmentTypes(parsed.appointmentTypes);
        if (parsed.selectedStylists) setSelectedStylists(parsed.selectedStylists);
        if (parsed.filters) setFilters(parsed.filters);
        if (parsed.hideCompleted !== undefined) setHideCompleted(parsed.hideCompleted);
        if (parsed.reportData) setReportData(parsed.reportData);
        if (parsed.hiddenOrders) setHiddenOrders(new Set(parsed.hiddenOrders));
        if (parsed.hiddenAppointments) setHiddenAppointments(new Set(parsed.hiddenAppointments));
        if (parsed.hiddenDays) setHiddenDays(new Set(parsed.hiddenDays));
        if (parsed.customNextRooms) setCustomNextRooms(parsed.customNextRooms);
        if (parsed.collapsedDays) setCollapsedDays(new Set(parsed.collapsedDays));
        console.log('📋 Restored report state from localStorage');
      }
    } catch (error) {
      console.error('Error loading saved report state:', error);
    }
  }, []);

  // Save state to localStorage whenever key values change
  useEffect(() => {
    // Only save if we have report data (don't save empty state)
    if (reportData) {
      try {
        const stateToSave = {
          startDate,
          endDate,
          offices,
          appointmentTypes,
          selectedStylists,
          filters,
          hideCompleted,
          reportData,
          hiddenOrders: Array.from(hiddenOrders),
          hiddenAppointments: Array.from(hiddenAppointments),
          hiddenDays: Array.from(hiddenDays),
          customNextRooms,
          collapsedDays: Array.from(collapsedDays),
          savedAt: new Date().toISOString()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
      } catch (error) {
        console.error('Error saving report state:', error);
      }
    }
  }, [startDate, endDate, offices, appointmentTypes, selectedStylists, filters, hideCompleted, reportData, hiddenOrders, hiddenAppointments, hiddenDays, customNextRooms, collapsedDays]);

  // Clear saved state function
  const clearSavedState = () => {
    localStorage.removeItem(STORAGE_KEY);
    setStartDate('');
    setEndDate('');
    setOffices([]);
    setAppointmentTypes([]);
    setSelectedStylists([]);
    setFilters({ roomNo: '', assignedWorker: '', customerName: '', meetingWith: '' });
    setHideCompleted(false);
    setReportData(null);
    setFilteredData(null);
    setHiddenOrders(new Set());
    setHiddenAppointments(new Set());
    setCustomNextRooms({});
    setCollapsedDays(new Set());
    console.log('🗑️ Cleared saved report state');
  };

  // Apply filters to report data (client-side) - works with nested appointment/wig structure
  const applyFilters = (data) => {
    if (!data || !data.data) return data;

    let filtered = data.data.map(appt => {
      // Filter wigs within each appointment
      let filteredWigs = [...(appt.wigs || [])];

      // Filter out completed orders if checkbox is checked
      if (hideCompleted) {
        filteredWigs = filteredWigs.filter(wig => {
          const room = String(wig.currentRoom || '').toLowerCase().trim();
          // Filter out: 'completed', '7001', or empty/null (which display as Completed)
          if (!room || room === 'completed' || room === '7001') {
            return false;
          }
          return true;
        });
      }

      // Filter out manually hidden orders
      if (hiddenOrders.size > 0) {
        filteredWigs = filteredWigs.filter(wig => !hiddenOrders.has(wig.orderNo));
      }

      // Filter by room number
      if (filters.roomNo) {
        const filterValue = filters.roomNo.trim().replace(/^room\s*/i, '');
        filteredWigs = filteredWigs.filter(wig => {
          if (!wig.currentRoom) return false;
          return wig.currentRoom.toString().includes(filterValue);
        });
      }

      // Filter by assigned worker
      if (filters.assignedWorker) {
        filteredWigs = filteredWigs.filter(wig =>
          wig.assignedWorker && wig.assignedWorker.toLowerCase().includes(filters.assignedWorker.toLowerCase())
        );
      }

      return { ...appt, wigs: filteredWigs };
    });

    // Filter out manually hidden appointments
    if (hiddenAppointments.size > 0) {
      filtered = filtered.filter(appt => !hiddenAppointments.has(appt.appointmentId));
    }

    // Filter by customer name (appointment level)
    if (filters.customerName) {
      filtered = filtered.filter(appt =>
        appt.customerName && appt.customerName.toLowerCase().includes(filters.customerName.toLowerCase())
      );
    }

    // Filter by meeting with (appointment level)
    if (filters.meetingWith) {
      filtered = filtered.filter(appt =>
        appt.meetingWith && appt.meetingWith.toLowerCase().includes(filters.meetingWith.toLowerCase())
      );
    }

    // Filter by selected stylist(s) - only meaningful once an office is chosen above
    if (selectedStylists.length > 0) {
      filtered = filtered.filter(appt =>
        appt.meetingWith && selectedStylists.some(name => name.toLowerCase() === appt.meetingWith.toLowerCase())
      );
    }

    // Count total wigs
    const totalWigs = filtered.reduce((sum, appt) => sum + (appt.wigs?.length || 0), 0);

    return {
      ...data,
      data: filtered,
      count: filtered.length,
      wigCount: totalWigs,
      filteredCount: filtered.length,
      originalCount: data.data.length
    };
  };

  // Refresh a single wig's data without reloading the entire report
  const refreshWigData = useCallback(async (orderNo) => {
    if (!orderNo) return;

    try {
      console.log(`🔄 Refreshing wig data for order ${orderNo}`);

      // Fetch updated wig data for this specific order
      const response = await fetch(`${API_URL}/api/orders/wig-summary/${orderNo}`);

      if (!response.ok) {
        console.error('Failed to fetch updated wig data');
        return;
      }

      const updatedWig = await response.json();

      // Update the wig data in reportData
      setReportData(prev => {
        if (!prev || !prev.data) return prev;

        return {
          ...prev,
          data: prev.data.map(appt => ({
            ...appt,
            wigs: appt.wigs.map(wig =>
              wig.orderNo === orderNo
                ? { ...wig, ...updatedWig }
                : wig
            )
          }))
        };
      });

      console.log(`✅ Wig data refreshed for order ${orderNo}`);
    } catch (error) {
      console.error('Error refreshing wig data:', error);
    }
  }, []);

  // Update appointment notes
  const updateAppointmentNotes = useCallback(async (appointmentId, notes) => {
    try {
      console.log(`📝 Updating notes for appointment ${appointmentId}`);

      const response = await fetch(`${API_URL}/api/rachels-room/appointment/${appointmentId}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes })
      });

      if (!response.ok) {
        throw new Error('Failed to update appointment notes');
      }

      // Update local state
      setReportData(prev => {
        if (!prev || !prev.data) return prev;

        return {
          ...prev,
          data: prev.data.map(appt =>
            appt.appointmentId === appointmentId
              ? { ...appt, appointmentNotes: notes }
              : appt
          )
        };
      });

      console.log(`✅ Notes updated for appointment ${appointmentId}`);
    } catch (error) {
      console.error('Error updating appointment notes:', error);
      throw error;
    }
  }, []);

  const updateEventDesc = useCallback(async (appointmentId, eventDesc) => {
    try {
      console.log(`📝 Updating notes for appointment ${appointmentId}`);

      const response = await fetch(`${API_URL}/api/rachels-room/appointment/${appointmentId}/eventDesc`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventDesc })
      });

      if (!response.ok) {
        throw new Error('Failed to update appointment eventDesc');
      }

      // Update local state
      setReportData(prev => {
        if (!prev || !prev.data) return prev;

        return {
          ...prev,
          data: prev.data.map(appt =>
            appt.appointmentId === appointmentId
              ? { ...appt, EventDesc: eventDesc }
              : appt
          )
        };
      });

      console.log(`✅ EventDesc updated for appointment ${appointmentId} : ${eventDesc}`);
    } catch (error) {
      console.error('Error updating appointment notes:', error);
      throw error;
    }
  }, []);



  // Listen for wig transfer and auto-complete events to refresh subrow data
  useEffect(() => {
    const handleWigTransfer = (event) => {
      const { orderNo } = event.detail || {};
      if (orderNo && expandedOrderNo === orderNo) {
        refreshWigData(orderNo);
      }
    };

    const handleAutoComplete = (event) => {
      const { orderNo } = event.detail || {};
      if (orderNo) {
        refreshWigData(orderNo);
      }
    };

    window.addEventListener('wigTransferCompleted', handleWigTransfer);
    window.addEventListener('orderAutoCompleted', handleAutoComplete);

    return () => {
      window.removeEventListener('wigTransferCompleted', handleWigTransfer);
      window.removeEventListener('orderAutoCompleted', handleAutoComplete);
    };
  }, [expandedOrderNo, refreshWigData]);

  // Update filtered data when filters change
  React.useEffect(() => {
    if (reportData) {
      const filtered = applyFilters(reportData);
      setFilteredData(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, reportData, hideCompleted, hiddenOrders, hiddenAppointments, selectedStylists]);

  // Lookout List - client-only, shared across pages via localStorage (see utils/lookoutList.js)
  const { isBookmarked, toggle: toggleLookout } = useLookoutList();

  const handleToggleLookout = (wig, appt) => {
    toggleLookout({
      orderNo: wig.orderNo,
      barcode: wig.barcode,
      customerName: appt.customerName,
      dueDate: wig.dueDate,
      currentRoom: wig.currentRoom,
      assignedWorker: wig.assignedWorker,
      nextRooms: wig.nextRooms,
      meetingWith: appt.meetingWith,
      appointmentDate: appt.appointmentDate,
      // This row IS the appointment record - not a guess, unlike the lookup
      // OrdersList.jsx has to do when bookmarking from the main orders table.
      appointmentMatchType: 'confirmed'
    });
  };

  // Generate report with streaming and progress
  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }

    setLoading(true);
    setError(null);
    setProgress({ current: 0, total: 0, percentage: 0 });

    try {
      // Use the appointment-centric endpoint (non-streaming)
      const response = await fetch(`${API_URL}/api/rachels-room/date-range-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate,
          endDate,
          offices: offices.length > 0 ? offices : null,
          appointmentTypes: appointmentTypes.length > 0 ? appointmentTypes : null,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate report: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to generate report');
      }

      console.log(`📊 Report loaded: ${result.count} appointments, ${result.wigCount} wigs`);
      setReportData(result);

    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0, percentage: 0 });
    }
  };

  // Export to Google Sheets
  const handleExportToSheets = async () => {
    const dataToExport = filteredData || reportData;

    if (!dataToExport || !dataToExport.data || dataToExport.data.length === 0) {
      setError('No data to export. Please generate a report first.');
      return;
    }

    setExporting(true);
    setError(null);

    try {
      // 1. Format data with hierarchical structure grouped by day, sorted by meetingWith
      const headers = [
        'Customer Name',
        'Order No',
        'Barcode',
        'Due Date',
        'Current Room',
        'Next Rooms',
        'Assigned Worker',
        'Description',
        'Notes',
        'Appointment Date',
        'Appointment Type',
        'Meeting With',
        'Status'
      ];

      const rows = [];
      const groupedData = groupByDayAndPerson(dataToExport.data);

      groupedData.forEach((dayGroup, dayIndex) => {
        // Day header row - marked with special prefix for Google Sheets formatting
        rows.push([
          `__DAY_HEADER__${dayGroup.day}`,
          '', '', '', '', '', '', '', '', '', '', '', ''
        ]);

        // Appointments for this day (already sorted: Dini first, Send Out last, alphabetical in between)
        dayGroup.appointments.forEach(appt => {
          // Appointment header row
          rows.push([
            appt.customerName || '',
            'Order No',
            'Barcode',
            'Due Date',
            'Current Room',
            'Next Rooms',
            'Assigned Worker',
            appt.eventDescription || '',
            appt.appointmentNotes || '',
            appt.appointmentDate ? formatDateTime(appt.appointmentDate) : '',
            appt.appointmentType || '',
            appt.meetingWith || '',
            appt.status || ''
          ]);

          // Wig sub-rows (exclude hidden orders, use custom next rooms if set)
          if (appt.wigs && appt.wigs.length > 0) {
            appt.wigs
              .filter(wig => !hiddenOrders.has(wig.orderNo))
              .forEach(wig => {
                const nextRoomsValue = customNextRooms[wig.orderNo] !== undefined
                  ? customNextRooms[wig.orderNo]
                  : filterNextRooms(wig.nextRooms);
                rows.push([
                  '',
                  wig.orderNo || '',
                  wig.barcode || '',
                  wig.dueDate ? formatDate(wig.dueDate) : '',
                  wig.currentRoom || '',
                  nextRoomsValue,
                  wig.assignedWorker || '',
                  '', '', '', '', '', ''
                ]);
              });
          }
        });

        // Day divider row (except for last day)
        if (dayIndex < groupedData.length - 1) {
          rows.push([
            '__DAY_DIVIDER__',
            '', '', '', '', '', '', '', '', '', '', '', ''
          ]);
        }
      });

      const sheetData = [headers, ...rows];

      console.log(`Exporting ${rows.length} records (${filteredData ? 'filtered' : 'all'})`);

      // 2. Convert to CSV format
      const csvRows = sheetData.map(row =>
        row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(',')
      ).join('\n');

      // 3. Use Form POST to submit to Google Sheets (avoids CORS and URL length limits)
      console.log('Submitting to Google Apps Script via POST');

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = GOOGLE_SHEETS_URL;
      form.target = '_blank';
      form.style.display = 'none';

      // Build report name with filter info
      let reportName = `Date_Range_${startDate}_to_${endDate}`;
      if (filteredData && filteredData.filteredCount !== filteredData.originalCount) {
        reportName += '_FILTERED';
      }

      // Add roomNo parameter (required by Google Apps Script)
      const roomInput = document.createElement('input');
      roomInput.type = 'hidden';
      roomInput.name = 'roomNo';
      roomInput.value = reportName;
      form.appendChild(roomInput);

      // Add CSV data parameter
      const dataInput = document.createElement('input');
      dataInput.type = 'hidden';
      dataInput.name = 'data';
      dataInput.value = csvRows;
      form.appendChild(dataInput);

      // Submit the form
      document.body.appendChild(form);
      form.submit();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(form);
      }, 1000);

      console.log('✅ Google Sheet export initiated');

    } catch (err) {
      console.error('Error exporting to Google Sheets:', err);
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  // Format date and time for appointments (without seconds)
  // SQL Server dates are stored as local time (EST) but mssql serializes them as UTC
  // Remove the 'Z' suffix to parse as local time instead of UTC
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    // Remove 'Z' suffix if present to treat as local time
    const localDateString = String(dateString).replace('Z', '');
    return new Date(localDateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Group appointments by day, then by meetingWith person
  // Sort: 'Dini' first, 'Send Out' last, alphabetically in between
  const groupByDayAndPerson = (data) => {
    if (!data || !data.length) return [];

    // Group by date (day only)
    const dayGroups = {};
    data.forEach(appt => {
      const dateKey = appt.appointmentDate
        ? new Date(String(appt.appointmentDate).replace('Z', '')).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })
        : 'No Date';

      if (!dayGroups[dateKey]) {
        dayGroups[dateKey] = [];
      }
      dayGroups[dateKey].push(appt);
    });

    // Sort meetingWith within each day
    const sortMeetingWith = (a, b) => {
      const meetingA = (a.meetingWith || '').toLowerCase();
      const meetingB = (b.meetingWith || '').toLowerCase();

      // 'Dini' always first
      if (meetingA === 'dini') return -1;
      if (meetingB === 'dini') return 1;

      // 'Send Out' always last
      if (meetingA === 'send out') return 1;
      if (meetingB === 'send out') return -1;

      // Alphabetical for the rest
      return meetingA.localeCompare(meetingB);
    };

    // Sort each day's appointments by meetingWith
    Object.keys(dayGroups).forEach(day => {
      dayGroups[day].sort(sortMeetingWith);
    });

    // Sort days chronologically
    const sortedDays = Object.keys(dayGroups).sort((a, b) => {
      if (a === 'No Date') return 1;
      if (b === 'No Date') return -1;
      return new Date(a) - new Date(b);
    });

    return sortedDays.map(day => ({
      day,
      appointments: dayGroups[day]
    }));
  };

  const SimpleBalanceDisplay = ({ orderNo }) => {
    const [userRequestedLoad, setUserRequestedLoad] = useState(false);
    
    const { balanceDue, loading, error } = useOrderBalance(orderNo, userRequestedLoad);
  
    if (!userRequestedLoad) {
      return (
        <div className="summary-item balance-summary">
          {/* <i className="fas fa-dollar-sign summary-icon"></i> */}
          <div className="summary-content">
            {/* <span className="summary-label">Balance Due:</span> */}
            <span 
              className="summary-value balance-badge clickable"
              onClick={() => setUserRequestedLoad(true)}
              title="Click to load balance"
            >
              <i className="fas fa-dollar-sign summary-icon"> Click to view</i> 
            </span>
          </div>
        </div>
      );
    }
  
    if (loading) {
      return (
        <div className="summary-item balance-summary">
          {/* <i className="fas fa-dollar-sign summary-icon"></i> */}
          <div className="summary-content">
            <span className="summary-label">Balance Due:</span>
            <span className="summary-value loading">
              <i className="fas fa-spinner fa-spin"></i> Loading...
            </span>
          </div>
        </div>
      );
    }
  
    if (error) {
      return (
        <div className="summary-item balance-summary">
          <i className="fas fa-dollar-sign summary-icon"></i>
          <div className="summary-content">
            <span className="summary-label">Balance Due:</span>
            <span className="summary-value error">
              <i className="fas fa-exclamation-triangle"></i> Unavailable
            </span>
          </div>
        </div>
      );
    }
  
    return (
      <div className="summary-item balance-summary">
        {/* <i className="fas fa-dollar-sign summary-icon"></i> */}
        <div className="summary-content">
          <span className="summary-label">Balance Due:</span>
          <span className={`summary-value ${balanceDue > 0 ? 'amount-due' : 'amount-paid'}`}>
            {formatCurrency(Math.abs(balanceDue))}
            {balanceDue < 0 && <small>(Credit)</small>}
            {balanceDue === 0 && <span ></span>}
          </span>
        </div>
      </div>
    );
  };



  return (
    <div className="date-range-report-container">
      <div className="report-header">
        {/* <p className="report-description">
          Generate a summary of all wigs due within a date range, showing their current location and appointment information
          </p> */}
              <div className="date-range-inputs">
          <h3>
            <i className="fas fa-calendar-alt"></i> Production Report
          </h3>
        <div className="input-group">
          <label htmlFor="start-date">Start Date:</label>
          <input
            id="start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="date-input"
          />
        </div>

        <div className="input-group">
          <label htmlFor="end-date">End Date:</label>
          <input
            id="end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="date-input"
          />
        </div>

        <div className="input-group" style={{ position: 'relative' }}>
          <label htmlFor="office-dropdown">Office:</label>
          <div ref={officeDropdownRef} style={{ position: 'relative' }}>
            <div
              onClick={() => setIsOfficeDropdownOpen(!isOfficeDropdownOpen)}
              className="date-input"
              style={{
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                minHeight: '38px',
                minWidth: '220px',
                padding: '8px 12px',
                backgroundColor: 'var(--dark-bg)'
              }}
            >
              <span>
                {offices.length === 0
                  ? 'All Offices'
                  : `${offices.length} selected`}
              </span>
              <i className={`fas fa-chevron-${isOfficeDropdownOpen ? 'up' : 'down'}`}></i>
            </div>

            {isOfficeDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'var(--dark-bg)',
                border: '1px solid #ccc',
                borderRadius: '4px',
                marginTop: '4px',
                maxHeight: '250px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                <div style={{ padding: '8px' }}>

                  {officeOptions.map(office => (
                    <label
                      key={office}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background-color 0.15s',
                        userSelect: 'none'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={offices.includes(office)}
                        onChange={() => handleOfficeToggle(office)}
                        style={{ marginRight: '10px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px' }}>{office}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="input-group" style={{ position: 'relative' }}>
          <label htmlFor="stylist-dropdown">
            Stylist:
          </label>
          <div ref={stylistDropdownRef} style={{ position: 'relative' }}>
            <div
              onClick={() => isStylistFilterEnabled && setIsStylistDropdownOpen(!isStylistDropdownOpen)}
              className="date-input"
              title={isStylistFilterEnabled ? undefined : 'Select an office first'}
              style={{
                cursor: isStylistFilterEnabled ? 'pointer' : 'not-allowed',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                minHeight: '38px',
                minWidth: '220px',
                padding: '8px 12px',
                backgroundColor: 'var(--dark-bg)',
                opacity: isStylistFilterEnabled ? 1 : 0.5
              }}
            >
              <span style={{ color: selectedStylists.length === 0 ? '#999' : 'var(--text-primary)' }}>
                {!isStylistFilterEnabled
                  ? 'Select an office first'
                  : selectedStylists.length === 0
                    ? 'All Stylists'
                    : `${selectedStylists.length} selected`}
              </span>
              <i className={`fas fa-chevron-${isStylistDropdownOpen ? 'up' : 'down'}`}></i>
            </div>

            {isStylistFilterEnabled && isStylistDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'var(--dark-bg)',
                border: '1px solid #ccc',
                borderRadius: '4px',
                marginTop: '4px',
                maxHeight: '250px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                <div style={{ padding: '8px' }}>
                  {availableStylists.map(name => (
                    <label
                      key={name}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background-color 0.15s',
                        userSelect: 'none'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={selectedStylists.includes(name)}
                        onChange={() => handleStylistToggle(name)}
                        style={{ marginRight: '10px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px' }}>{name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="input-group" style={{ position: 'relative' }}>
          <label htmlFor="appointment-types-dropdown">Appointment Types:</label>
          <div ref={appointmentDropdownRef} style={{ position: 'relative' }}>
            <div
              onClick={() => setIsAppointmentDropdownOpen(!isAppointmentDropdownOpen)}
              className="date-input"
              style={{
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                minHeight: '38px',
                minWidth: '220px',
                padding: '8px 12px',
                backgroundColor: 'var(--dark-bg)'
              }}
            >
              <span style={{ color: appointmentTypes.length === 0 ? '#999' : 'var(--text-primary)' }}>
                {appointmentTypes.length === 0
                  ? 'All Appointment Types'
                  : `${appointmentTypes.length} selected`}
              </span>
              <i className={`fas fa-chevron-${isAppointmentDropdownOpen ? 'up' : 'down'}`}></i>
            </div>

            {isAppointmentDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'var(--dark-bg)',
                border: '1px solid #ccc',
                borderRadius: '4px',
                marginTop: '4px',
                maxHeight: '250px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}>
                <div style={{ padding: '8px' }}>
               
                  {APPOINTMENT_TITLES.map(type => (
                    <label
                      key={type}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background-color 0.15s',
                        userSelect: 'none'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={appointmentTypes.includes(type)}
                        onChange={() => handleAppointmentTypeToggle(type)}
                        style={{ marginRight: '10px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px' }}>{type}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="input-group">
          <button
            className="btn btn-primary"
            onClick={handleGenerateReport}
            disabled={loading || !startDate || !endDate}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
              </>
            ) : (
              <>
                <i className="fas fa-chart-bar"></i> 
              </>
            )}
          </button>

          {reportData && reportData.data && reportData.data.length > 0 && (
            <button
              className="btn btn-success"
              onClick={handleExportToSheets}
              disabled={exporting}
            >
              {exporting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Exporting...
                </>
              ) : (
                <>
                  <i className="fas fa-file-excel"></i> Export to Google Sheets
                </>
              )}
            </button>
          )}

          {reportData && (
            <button
              className="btn"
              onClick={clearSavedState}
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                marginLeft: '10px'
              }}
              title="Clear saved report and start fresh"
            >
              <i className="fas fa-eraser"></i> Clear Report
            </button>
          )}
        </div>

        {/* Progress Bar Overlay Modal */}
        {loading && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              backgroundColor: 'var(--dark-bg)',
              borderRadius: '16px',
              padding: '35px 45px',
              minWidth: '400px',
              maxWidth: '500px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              animation: 'fadeIn 0.3s ease-in'
            }}>
              {/* Header */}
              <div style={{ marginBottom: '25px', textAlign: 'center' }}>
                <i className="fas fa-chart-line" style={{ fontSize: '40px', color: '#007bff', marginBottom: '22px', display: 'block' }}></i>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  {progress.total > 0 ? 'Generating Report' : 'Preparing Report'}
                </h3>
                <p style={{ margin: 0, fontSize: '15px', color: 'var(--text-secondary)' }}>
                  {progress.total > 0
                    ? `Processing records in batches...`
                    : 'Counting total records...'}
                </p>
              </div>
           
            </div>          </div>
        )}
      </div>
      </div>



      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i> {error}
        </div>
      )}


{/* Report Summary - Collapsible Section */}
      {reportData && (
        <div className="report-summary" style={{
          marginBottom: '15px',
          padding: '12px 15px',
          background: 'var(--dark-bg)',
          borderRadius: '8px',
          border: '1px solid rgba(212, 175, 55, 0.3)'
        }}>
          <div
            onClick={() => setShowReportSummary(prev => !prev)}
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <i className={`fas fa-chevron-${showReportSummary ? 'down' : 'right'}`} style={{ marginRight: '10px', color: '#d4af37' }}></i>
            <i className="fas fa-chart-bar" style={{ marginRight: '8px', color: '#d4af37' }}></i>
            <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
              Report Summary ({(filteredData || reportData).count} appointments, {(filteredData || reportData).wigCount || 0} wigs)
            </span>
            <span style={{ marginLeft: '10px', fontSize: '0.85em', color: 'var(--text-secondary)' }}>
              {formatDate(reportData.startDate)} - {formatDate(reportData.endDate)}
            </span>
          </div>

          {showReportSummary && (
        <div className="report-results">

          {/* Room Legend */}
          <div style={{
            marginBottom: '15px',
            padding: '12px 15px',
            background: 'var(--dark-bg)',
            borderRadius: '8px',
            border: '1px solid rgba(212, 175, 55, 0.3)'
          }}>
            <div
              onClick={() => setShowLegend(prev => !prev)}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              <i className={`fas fa-chevron-${showLegend ? 'down' : 'right'}`} style={{ marginRight: '10px', color: '#d4af37' }}></i>
              <i className="fas fa-key" style={{ marginRight: '8px', color: '#d4af37' }}></i>
              <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>Room Legend</span>
            </div>
            {showLegend && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '8px 20px',
                marginTop: '12px',
                paddingTop: '12px',
                borderTop: '1px solid rgba(212, 175, 55, 0.2)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span className="room-badge not-started" style={{ fontSize: '14px', padding: '2px 8px' }}>9001</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>Prep</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="room-badge" style={{ fontSize: '14px', padding: '2px 8px' }}>6001</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>Rachel's Room</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="room-badge" style={{ fontSize: '14px', padding: '2px 8px' }}>1001</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>Wefts</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="room-badge" style={{ fontSize: '14px', padding: '2px 8px' }}>1002</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>Machine</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="room-badge" style={{ fontSize: '14px', padding: '2px 8px' }}>3001</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>Handsew</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="room-badge" style={{ fontSize: '14px', padding: '2px 8px' }}>2002</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>Color/Knots</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="room-badge" style={{ fontSize: '14px', padding: '2px 8px' }}>4001</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>Treatment</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="room-badge washed" style={{ fontSize: '14px', padding: '2px 8px' }}>5001</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>Wash & Set</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="room-badge" style={{ fontSize: '14px', padding: '2px 8px' }}>3002</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>California</span>
                </div>
                {/* <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="room-badge completed" style={{ fontSize: '14px', padding: '2px 8px' }}>7001</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>Completed</span>
                </div> */}
              </div>
            )}
          </div>

          {/* Filter Controls */}
          <div className="filter-controls" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px',
            marginBottom: '20px',
            padding: '15px',
            background: 'var(--dark-bg)',
            borderRadius: '5px'
          }}>
            <div className="filter-group">
              <label htmlFor="filter-room" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                <i className="fas fa-door-open"></i> Room:
              </label>
              <input
                id="filter-room"
                type="text"
                placeholder="Filter by room..."
                value={filters.roomNo}
                onChange={(e) => setFilters({ ...filters, roomNo: e.target.value })}
                className="date-input"
                style={{ width: '100%' }}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="filter-worker" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                <i className="fas fa-user"></i> Worker:
              </label>
              <input
                id="filter-worker"
                type="text"
                placeholder="Filter by worker..."
                value={filters.assignedWorker}
                onChange={(e) => setFilters({ ...filters, assignedWorker: e.target.value })}
                className="date-input"
                style={{ width: '100%' }}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="filter-customer" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                <i className="fas fa-user-tag"></i> Customer:
              </label>
              <input
                id="filter-customer"
                type="text"
                placeholder="Filter by customer..."
                value={filters.customerName}
                onChange={(e) => setFilters({ ...filters, customerName: e.target.value })}
                className="date-input"
                style={{ width: '100%' }}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="filter-meeting" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                <i className="fas fa-handshake"></i> Meeting With:
              </label>
              <input
                id="filter-meeting"
                type="text"
                placeholder="Filter by meeting with..."
                value={filters.meetingWith}
                onChange={(e) => setFilters({ ...filters, meetingWith: e.target.value })}
                className="date-input"
                style={{ width: '100%' }}
              />
            </div>

            <div className="filter-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '8px 12px',
                backgroundColor: hideCompleted ? '#28a745' : 'transparent',
                border: '1px solid #ccc',
                borderRadius: '4px',
                transition: 'all 0.2s'
              }}>
                <input
                  type="checkbox"
                  checked={hideCompleted}
                  onChange={(e) => setHideCompleted(e.target.checked)}
                  style={{ marginRight: '8px', cursor: 'pointer' }}
                />
                <i className="fas fa-check-circle" style={{ marginRight: '6px', color: hideCompleted ? 'white' : '#28a745' }}></i>
                <span style={{ color: hideCompleted ? 'white' : 'inherit' }}>Hide Completed</span>
              </label>
            </div>

            {(filters.roomNo || filters.assignedWorker || filters.customerName || filters.meetingWith || hiddenOrders.size > 0 || hiddenAppointments.size > 0 || hiddenDays.size > 0) && (
              <div className="filter-group" style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                <button
                  onClick={() => {
                    setFilters({ roomNo: '', assignedWorker: '', customerName: '', meetingWith: '' });
                    setHiddenOrders(new Set());
                    setHiddenAppointments(new Set());
                    setHiddenDays(new Set());
                  }}
                  className="btn"
                  style={{
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '8px 15px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  <i className="fas fa-times"></i> Clear Filters
                  {(hiddenOrders.size > 0 || hiddenAppointments.size > 0 || hiddenDays.size > 0) && ` (${hiddenOrders.size + hiddenAppointments.size + hiddenDays.size} hidden)`}
                </button>
              </div>
            )}
          </div>

          {(filteredData || reportData).data && (filteredData || reportData).data.length > 0 ? (
            <div className="table-responsive">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Customer Name</th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th>Description</th>
                    <th>Notes</th>
                    <th>Appointment Date</th>
                    <th>Appointment Type</th>
                    <th>Meeting With</th>
                    <th>Status</th>
                    <th style={{ width: '40px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {groupByDayAndPerson((filteredData || reportData).data).filter(dayGroup => !hiddenDays.has(dayGroup.day)).map((dayGroup, dayIndex) => (
                    <React.Fragment key={dayGroup.day}>
                      {/* Day Header Row - Clickable to collapse/expand */}
                      <tr
                        className={`day-header-row ${collapsedDays.has(dayGroup.day) ? 'collapsed' : ''}`}
                        onClick={() => toggleDayCollapse(dayGroup.day)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td colSpan="14">
                          <i className={`fas fa-chevron-${collapsedDays.has(dayGroup.day) ? 'right' : 'down'}`} style={{ marginRight: '10px' }}></i>
                          <i className="fas fa-calendar-day"></i> {dayGroup.day}
                          <span style={{ marginLeft: '15px', fontSize: '12px', opacity: 0.7 }}>
                            ({dayGroup.appointments.length} appointments)
                        
                          </span>
                            <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setHiddenDays(prev => new Set([...prev, dayGroup.day]));
                                    }}
                                    title="Hide this day from view"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      color: '#ff6b6b',
                                      cursor: 'pointer',
                                      padding: '4px 8px',
                                      fontSize: '14px',
                                      opacity: 0.8,
                                      transition: 'opacity 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.opacity = 1}
                                    onMouseLeave={(e) => e.target.style.opacity = 0.7}
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                        </td>
                      </tr>
                      {/* Appointments for this day - hidden when collapsed */}
                      {!collapsedDays.has(dayGroup.day) && dayGroup.appointments.map((appt, apptIndex) => (
                        <React.Fragment key={appt.appointmentId || `${dayIndex}-${apptIndex}`}>
                          {/* Appointment Header Row */}
                          <tr className="appointment-row">
                            <td className="customer-name">
                              <a
                                href={`/customers/${appt.clientId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="order-link-r"
                              >
                                {appt.customerName}
                              </a>
                            </td>
                            <td className="wig-header">Order No</td>
                            <td className="wig-header">Barcode</td>
                            <td className="wig-header">Due Date</td>
                            <td className="wig-header">Current Room</td>
                            <td className="wig-header">Next Rooms</td>
                            <td className="wig-header">Assigned Worker</td>
                            <td style={{ maxWidth: '200px', fontSize: '16px' }}>
                              <EditableCell
                                value= {appt.EventDesc || ''}
                                onSave={(value) => updateEventDesc(appt.appointmentId, value)}
                                type="text"
                                multiline={false}
                                placeholder="Event Desc..."
                                className="notes-cell"
                              />

                            </td>
                            <td onClick={(e) => e.stopPropagation()} style={{ minWidth: '150px', maxWidth: '250px' }}>
                              <EditableCell
                                value={appt.appointmentNotes || ''}
                                onSave={(value) => updateAppointmentNotes(appt.appointmentId, value)}
                                type="text"
                                multiline={true}
                                placeholder="Appointment Notes..."
                                className="notes-cell"
                              />
                            </td>
                            <td>{appt.appointmentDate ? formatDateTime(appt.appointmentDate) : '-'}</td>
                            <td>{appt.appointmentType || '-'}</td>
                            <td>{appt.meetingWith || '-'}</td>
                            <td>
                              <span className={`status-badge ${(appt.status || 'unknown').toLowerCase().replace(/\s+/g, '-')}`}>
                                {appt.status || 'Unknown'}
                              </span>
                            </td>
                            <td>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setHiddenAppointments(prev => new Set([...prev, appt.appointmentId]));
                                }}
                                title="Hide this appointment from view"
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#dc3545',
                                  cursor: 'pointer',
                                  padding: '4px 8px',
                                  fontSize: '14px',
                                  opacity: 0.7,
                                  transition: 'opacity 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.opacity = 1}
                                onMouseLeave={(e) => e.target.style.opacity = 0.7}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </td>
                          </tr>
                          {/* Wig Sub-Rows */}
                          {appt.wigs && appt.wigs.map((wig, wigIndex) => (
                            <React.Fragment key={`${appt.appointmentId}-${wig.orderNo}-${wigIndex}`}>
                              <tr
                                className={`wig-subrow ${expandedOrderNo === wig.orderNo ? 'expanded' : ''}`}
                                onClick={() => setExpandedOrderNo(expandedOrderNo === wig.orderNo ? null : wig.orderNo)}
                                style={{ cursor: 'pointer' }}
                              >
                                <td>
                                  <i className={`fas fa-chevron-${expandedOrderNo === wig.orderNo ? 'down' : 'right'}`} style={{ marginRight: '8px', color: '#666' }}></i>
                                </td>
                                <td>
                                  <a
                                    href={`/work-order-profile/${wig.orderNo}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="order-link-r"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {wig.orderNo}
                                  </a>
                                </td>
                                <td>{wig.barcode}</td>
                                <td>{wig.dueDate ? formatDate(wig.dueDate) : '-'}</td>
                                
                                <td>
                                  {String(wig.currentRoom) === '7001' ? (
                                    <span className="room-badge completed">Completed</span>
                                  ) : String(wig.currentRoom) === '9001' ? (
                                    <span className="room-badge not-started">Room 9001</span>
                                  ) : String(wig.currentRoom) === '5001' ? (
                                    <span className="room-badge washed">Room 5001</span>
                                  ) : wig.currentRoom ? (
                                    <span className="room-badge">Room {wig.currentRoom}</span>
                                  ) : (
                                    <span className="room-badge completed">Completed</span>
                                  )}
                                </td>
                                <td onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="text"
                                    value={customNextRooms[wig.orderNo] !== undefined ? customNextRooms[wig.orderNo] : filterNextRooms(wig.nextRooms)}
                                    onChange={(e) => setCustomNextRooms(prev => ({ ...prev, [wig.orderNo]: e.target.value }))}
                                    placeholder={filterNextRooms(wig.nextRooms) || 'Add notes...'}
                                    style={{
                                      width: '100%',
                                      padding: '4px 8px',
                                      border: '1px solid #ddd',
                                      borderRadius: '4px',
                                      backgroundColor: 'var(--dark-bg)',
                                      color: 'var(--text-primary)',
                                      fontSize: '16px'
                                    }}
                                  />
                                </td>
                                <td>{wig.assignedWorker || ''}</td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td onClick={(e) => e.stopPropagation()}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleLookout(wig, appt);
                                    }}
                                    title={isBookmarked(wig.orderNo) ? 'Remove from Lookout List' : 'Add to Lookout List'}
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      color: isBookmarked(wig.orderNo) ? '#d4af37' : '#999',
                                      cursor: 'pointer',
                                      padding: '4px 8px',
                                      fontSize: '14px',
                                      opacity: isBookmarked(wig.orderNo) ? 1 : 0.6,
                                      transition: 'opacity 0.2s, color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = isBookmarked(wig.orderNo) ? 1 : 0.6}
                                  >
                                    <i className={`${isBookmarked(wig.orderNo) ? 'fas' : 'far'} fa-bookmark`}></i>
                                  </button>
                                </td>
                                <td>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setHiddenOrders(prev => new Set([...prev, wig.orderNo]));
                                    }}
                                    title="Hide this order from view"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      color: '#dc3545',
                                      cursor: 'pointer',
                                      padding: '4px 8px',
                                      fontSize: '14px',
                                      opacity: 0.7,
                                      transition: 'opacity 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.opacity = 1}
                                    onMouseLeave={(e) => e.target.style.opacity = 0.7}
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </td>
                              </tr>
                              {/* Expanded ProductionSchedule Row */}
                              {expandedOrderNo === wig.orderNo && (
                                <tr className="production-schedule-row">
                                  <td colSpan="14" style={{ backgroundColor: 'var(--dark-bg)' }}>
                                    <div style={{ padding: '3px', borderLeft: '4px solid var(--purple-light)' }}>
                                      <ProductionSchedule
                                        orderNo={wig.orderNo}
                                        onAutoCompleteSuccess={() => refreshWigData(wig.orderNo)}
                                        onDataChange={() => refreshWigData(wig.orderNo)}
                                      />
                                      <SimpleBalanceDisplay orderNo={wig.orderNo} />
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </React.Fragment>
                      ))}
                      {/* Day Footer Row - collapse button at bottom, only when expanded */}
                      {!collapsedDays.has(dayGroup.day) && (
                        <tr
                          className="day-header-row"
                          onClick={() => toggleDayCollapse(dayGroup.day)}
                          style={{ cursor: 'pointer'  }}
                        >
                          <td colSpan="14" style={{ textAlign: 'right', fontSize: '12px', opacity: 0.8 }}>
                            <i className="fas fa-chevron-up" style={{ marginRight: '6px' }}></i>
                            Collapse {dayGroup.day}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setHiddenDays(prev => new Set([...prev, dayGroup.day]));
                              }}
                              title="Hide this day from view"
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#ff6b6b',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                fontSize: '14px',
                                opacity: 0.8,
                                transition: 'opacity 0.2s',
                                marginLeft: '10px'
                              }}
                              onMouseEnter={(e) => e.target.style.opacity = 1}
                              onMouseLeave={(e) => e.target.style.opacity = 0.8}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </td>
                        </tr>
                      )}
                      {/* Day Divider */}
                      {dayIndex < groupByDayAndPerson((filteredData || reportData).data).length - 1 && (
                        <tr className="day-divider-row">
                          <td colSpan="14"></td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-results">
              <i className="fas fa-info-circle"></i>
              <p>No appointments found matching the current filters</p>
            </div>
          )}
          {/* Bottom Report Summary bar - mirrors the top one */}
          <div
            onClick={() => {
              setShowReportSummary(prev => !prev);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              marginTop: '12px',
              padding: '12px 15px',
              background: 'var(--dark-bg)',
              borderRadius: '8px',
              border: '1px solid rgba(212, 175, 55, 0.3)'
            }}
          >
            <i className={`fas fa-chevron-${showReportSummary ? 'up' : 'right'}`} style={{ marginRight: '10px', color: '#d4af37' }}></i>
            <i className="fas fa-chart-bar" style={{ marginRight: '8px', color: '#d4af37' }}></i>
            <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
              Report Summary ({(filteredData || reportData).count} appointments, {(filteredData || reportData).wigCount || 0} wigs)
            </span>
            <span style={{ marginLeft: '10px', fontSize: '0.85em', color: 'var(--text-secondary)' }}>
              {formatDate(reportData.startDate)} - {formatDate(reportData.endDate)}
            </span>
          </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DateRangeReport;
